import Option, {some} from '@danwang/option';
import {
  TransferType,
  useAllCategoriesQuery,
  useAllFieldsQuery,
  useTransactionByIdQuery,
  useUpdateTransactionMutation,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import Money from 'common/Money';
import {CalendarDays, ChevronDown, Clock, Hash, PencilLine, X} from 'lucide-react';
import * as React from 'react';
import {AccountInlineDisplay} from 'src/AccountInlineDisplay';
import {AmountDisplay} from 'src/AmountDisplay';
import {addNewCategory} from 'src/apolloCache/updateCategories';
import {addPayee} from 'src/apolloCache/updatePayees';
import {addNewTags} from 'src/apolloCache/updateTags';
import {Button} from 'src/Button';
import {CategoryAvatar} from 'src/CategoryAvatar';
import CheckboxBase from 'src/Checkbox/Base';
import {
  DecisionPill,
  Expand,
  PayeeFace,
  ScreenFrame,
  SheetInputLine,
  SheetLine,
  SheetScreens,
  usePayeeLogos,
  valueClass,
} from 'src/CreateSheet/parts';
import {
  CategoryScreen,
  PayeeScreen,
  TransferTypeScreen,
  TransferTypeTile,
} from 'src/CreateSheet/screens';
import DatePicker from 'src/DatePicker/DatePicker';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import {TagsAndFieldsInput} from 'src/inputs/TagsAndFieldsInput/TagsAndFieldsInput';
import {totalsByCurrency} from 'src/lib/currencyTotals';
import {formatListDate} from 'src/lib/displayDate';
import {cn} from 'src/lib/utils';
import {Modal} from 'src/Modal';
import {TRANSFER_TYPE_LABELS} from 'src/TransferKindSelect';
import type {FetchResult} from '@apollo/client';
import type {
  BasicRawTransactionFragment,
  UnifiedTransactionFragment,
  UpdateTransactionMutation,
} from '@transactions/graphql';
import type {DateTime} from 'luxon';
import type {SystemProps} from 'src/Modals';

export type EditTransactionModalUserProps = {
  transaction: UnifiedTransactionFragment;
  /** Called after a successful save. */
  onSaved?: () => void;
};

type Draft = {
  payee: string;
  category: Option<string>;
  transferType: TransferType | null;
  date: DateTime;
  description: string;
  tags: string[];
  fieldValues: Record<string, string>;
  pending: boolean;
};

const draftOf = (transaction: UnifiedTransactionFragment): Draft => ({
  payee: transaction.payee ?? '',
  category: Option(transaction.category?.name),
  transferType: transaction.transferType ?? null,
  date: parseDate(transaction.date),
  description: transaction.description ?? '',
  tags: transaction.tags.map((tag) => tag.name),
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  fieldValues: (transaction.fieldValues || {}) as Record<string, string>,
  pending: transaction.pending,
});

const sameDraft = (a: Draft, b: Draft) =>
  a.payee === b.payee &&
  a.category.equals(b.category) &&
  a.transferType === b.transferType &&
  a.date.toISODate() === b.date.toISODate() &&
  a.description === b.description &&
  a.tags.join('\u0000') === b.tags.join('\u0000') &&
  JSON.stringify(a.fieldValues) === JSON.stringify(b.fieldValues) &&
  a.pending === b.pending;

/** A category's avatar, sized for the decision pill. */
const CategoryPillAvatar = ({name}: {name: string}) => {
  const {data} = useAllCategoriesQuery();
  const category = data?.allCategories.find((c) => c.name === name) ?? {name};
  return <CategoryAvatar category={category} className="size-6 rounded-md max-md:size-6" />;
};

/** A line the transaction is made of, as the create sheet lists it, read-only. */
const LineRow = ({
  raw,
  amount,
}: {
  raw: BasicRawTransactionFragment;
  amount: number;
}) => (
  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 px-3 py-2.5">
    <span className="truncate" title={raw.description}>
      {raw.description}
    </span>
    <span className="text-right tabular-nums">
      <AmountDisplay variant="plain" amount={Money.fromInt(amount, raw.currency)} />
    </span>
    <span className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-sm">
      <AccountInlineDisplay account={raw.account} />
    </span>
    <span className="text-muted-foreground text-right text-sm">
      {formatListDate(raw.date)}
    </span>
  </div>
);

/**
 * Edits a transaction in the create sheet's own layout: the payee and the
 * one decision its kind needs (a category, or a transfer type) up top, then
 * date, note, tags and fields, and pending, then the lines it is made of.
 * The lines are read-only here; moving money between transactions is
 * Merge's and Add to transaction's job.
 */
export const EditTransactionSheet = ({
  transaction,
  onSaved,
  onRequestClose,
}: EditTransactionModalUserProps & SystemProps) => {
  const formId = React.useId();
  const formatMoney = useMoneyFormatter();
  const payeeLogos = usePayeeLogos();
  const {data: fieldsData} = useAllFieldsQuery();
  // The lines: the list rows carry only their ids, so ask for the detail
  // (already cached when the sheet opens from the transaction's page).
  const {data: detailData} = useTransactionByIdQuery({
    variables: {id: transaction._id},
  });
  const lines = (detailData?.transactionById?.consumptions ?? []).flatMap(
    (consumption) =>
      consumption.rawTransaction
        ? [{raw: consumption.rawTransaction, amount: consumption.amount}]
        : [],
  );

  const original = React.useMemo(() => draftOf(transaction), [transaction]);
  const [draft, setDraft] = React.useState<Draft>(original);
  const set =
    <K extends keyof Draft>(key: K) =>
    (value: Draft[K]) =>
      setDraft((prev) => ({...prev, [key]: value}));

  const isPayment = transaction.netAmount !== 0;
  const isConversion = transaction.transferType === TransferType.Conversion;

  type Screen = 'payee' | 'category' | 'type' | 'tags';
  const [screen, setScreen] = React.useState<Screen | null>(null);
  const [dateOpen, setDateOpen] = React.useState(false);

  const [updateTransaction, {loading, error}] = useUpdateTransactionMutation();

  const hasChanges = !sameDraft(original, draft);
  // A payment needs its payee and category, as on create. A transfer's payee
  // is optional, and its type can be changed but not removed once it has one.
  const valid = isPayment
    ? draft.payee.trim() !== '' && !draft.category.isEmpty
    : original.transferType == null || draft.transferType != null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (screen != null || !hasChanges || !valid || loading) return;
    void updateTransaction({
      variables: {
        transaction: transaction._id,
        // An empty string clears a transfer's payee server-side.
        payee: draft.payee,
        description: draft.description,
        category: isPayment ? draft.category.getOrElse(null) : undefined,
        transferType:
          !isPayment && !isConversion ? draft.transferType : undefined,
        date: draft.date.toISODate(),
        pending: draft.pending,
        tags: draft.tags,
        fieldValues: draft.fieldValues,
      },
      update: (store, result: FetchResult<UpdateTransactionMutation>) => {
        const tx = result.data?.updateTransaction;
        if (!tx) return;
        if (tx.category) addNewCategory(store, tx.category);
        if (tx.tags) addNewTags(store, tx.tags);
        if (tx.payee) addPayee(store, tx.payee);
      },
    })
      .then(() => {
        onRequestClose();
        onSaved?.();
      })
      .catch(() => {
        // Surfaced through the mutation's error state below.
      });
  };

  // ---- Header ----
  const accountNames = (pick: (amount: number) => boolean) => [
    ...new Set(lines.filter((l) => pick(l.amount)).map((l) => l.raw.account.name)),
  ];
  const sideLabel = (names: string[]) =>
    names.length === 1 ? names[0] : `${names.length} accounts`;

  const title = (
    <button
      type="button"
      onClick={() => setScreen('payee')}
      className="hover:bg-muted -mx-1.5 flex min-w-0 cursor-pointer items-start gap-1 rounded-lg px-1.5 text-left"
    >
      <span
        className={cn(
          'line-clamp-2 min-w-0 text-[17px] leading-tight font-semibold break-words',
          draft.payee === '' && 'text-muted-foreground font-medium',
        )}
      >
        {draft.payee === '' ? 'Add payee' : draft.payee}
      </span>
      <ChevronDown className="mt-0.5 size-3.5 shrink-0 text-blue-600 dark:text-blue-400" />
    </button>
  );

  let face: React.ReactNode;
  let sub: React.ReactNode = null;
  let amount: React.ReactNode;
  let decision: React.ReactNode;
  if (isPayment) {
    face = <PayeeFace name={draft.payee} logo={payeeLogos.get(draft.payee)} />;
    sub = lines.length > 0 ? sideLabel(accountNames(() => true)) : null;
    amount = (
      <AmountDisplay
        variant="plain"
        amount={Money.fromInt(transaction.netAmount, transaction.currency)}
      />
    );
    const category = draft.category.getOrElse('');
    decision = (
      <DecisionPill
        onClick={() => setScreen('category')}
        leading={category !== '' ? <CategoryPillAvatar name={category} /> : undefined}
      >
        {category === '' ? (
          <span className="text-muted-foreground">Choose category</span>
        ) : (
          <span className="truncate font-semibold">{category}</span>
        )}
      </DecisionPill>
    );
  } else {
    face = (
      <TransferTypeTile type={draft.transferType} className="size-10 rounded-xl" />
    );
    sub =
      lines.length > 0
        ? `${sideLabel(accountNames((a) => a < 0))} → ${sideLabel(accountNames((a) => a > 0))}`
        : null;
    // A transfer nets to zero; what moved is one side. A conversion's two
    // sides are in two currencies, so it shows both.
    amount = isConversion
      ? totalsByCurrency(
          lines.map((l) => ({amount: l.amount, currency: l.raw.currency})),
        )
          .sort((a, b) => a.amount - b.amount)
          .map((leg) => formatMoney(leg))
          .join(' → ')
      : formatMoney(
          Money.fromInt(
            transaction.consumptions
              .filter((c) => c.amount > 0)
              .reduce((sum, c) => sum + c.amount, 0),
            transaction.currency,
          ),
        );
    decision = isConversion ? (
      <DecisionPill fixed leading={<TransferTypeTile type={TransferType.Conversion} />}>
        {TRANSFER_TYPE_LABELS[TransferType.Conversion]}
      </DecisionPill>
    ) : (
      <DecisionPill
        onClick={() => setScreen('type')}
        leading={<TransferTypeTile type={draft.transferType} />}
      >
        {draft.transferType == null ? (
          <span className="text-muted-foreground">Choose type</span>
        ) : (
          <span className="truncate font-semibold">
            {TRANSFER_TYPE_LABELS[draft.transferType]}
          </span>
        )}
      </DecisionPill>
    );
  }

  const header = (
    <div className="flex flex-col gap-3 pb-3">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5">
        <span className="row-span-2 flex">{face}</span>
        <span className="min-w-0">{title}</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={onRequestClose}
          aria-label="Close"
          className="row-span-2 -mr-2 self-start"
        >
          <X className="size-4" />
        </Button>
        <span className="text-muted-foreground min-w-0 truncate text-[12.5px]">{sub}</span>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
        {decision}
        <span className="text-2xl font-semibold tracking-tight whitespace-nowrap tabular-nums">
          {amount}
        </span>
      </div>
    </div>
  );

  // ---- Lines ----
  const fieldNames = new Map(
    (fieldsData?.allFields ?? []).map((field) => [field._id, field.name]),
  );
  const tagSummary = [
    ...draft.tags.map((tag) => ({key: `tag:${tag}`, label: <>{tag}</>})),
    ...Object.entries(draft.fieldValues)
      .filter(([, value]) => value != null && value !== '')
      .map(([fieldId, value]) => ({
        key: `field:${fieldId}`,
        label: (
          <>
            <span className="text-muted-foreground">
              {fieldNames.get(fieldId) ?? 'Field'}:
            </span>{' '}
            {value}
          </>
        ),
      })),
  ];
  const sheetLines = (
    <div className="border-border divide-border border-y [&>*+*]:border-t">
      <div>
        <SheetLine
          icon={<CalendarDays />}
          onClick={() => setDateOpen((open) => !open)}
          open={dateOpen}
        >
          <span className={valueClass(true)}>{draft.date.toFormat('LLL d, yyyy')}</span>
        </SheetLine>
        <Expand open={dateOpen}>
          <DatePicker selectedDate={draft.date} onChange={set('date')} />
        </Expand>
      </div>
      <SheetInputLine
        icon={<PencilLine />}
        value={draft.description}
        onChange={set('description')}
        placeholder="Add a note"
        onFocus={() => setDateOpen(false)}
      />
      <SheetLine icon={<Hash />} onClick={() => setScreen('tags')} toScreen wrap>
        {tagSummary.length === 0 ? (
          <span className="text-muted-foreground">Tags & fields</span>
        ) : (
          tagSummary.map((t) => (
            <span
              key={t.key}
              className="bg-muted border-border max-w-full truncate rounded-full border px-2 py-0.5 text-[13px] font-medium"
            >
              {t.label}
            </span>
          ))
        )}
      </SheetLine>
      <button
        type="button"
        role="checkbox"
        aria-checked={draft.pending}
        onClick={() => set('pending')(!draft.pending)}
        className="flex w-full cursor-pointer items-center gap-3 py-3 text-left"
      >
        <span className="text-muted-foreground flex w-5 shrink-0 justify-center [&>svg]:size-4">
          <Clock />
        </span>
        <span className={cn('flex-1', valueClass(draft.pending))}>Pending</span>
        <span className="pointer-events-none">
          <CheckboxBase value={draft.pending ? 'check' : 'empty'} />
        </span>
      </button>
    </div>
  );

  const lineList = lines.length > 0 && (
    <div className="flex flex-col gap-1.5 pt-4">
      <div className="text-muted-foreground px-1 text-xs font-semibold tracking-wider uppercase">
        {lines.length === 1 ? 'Line' : `${lines.length} lines`}
      </div>
      <div className="bg-muted/40 border-border divide-border overflow-hidden rounded-xl border [&>*+*]:border-t">
        {lines.map((line) => (
          <LineRow key={line.raw._id} raw={line.raw} amount={line.amount} />
        ))}
      </div>
    </div>
  );

  // ---- Second screens ----
  const renderScreen = (key: string) => {
    if (key === 'payee') {
      return (
        <PayeeScreen
          title="Payee"
          value={draft.payee}
          suggested={[]}
          logos={payeeLogos}
          onPick={(payee) => {
            set('payee')(payee);
            setScreen(null);
          }}
          // A transfer's payee is optional, so it can be taken away again.
          onClear={
            !isPayment && draft.payee !== ''
              ? () => {
                  set('payee')('');
                  setScreen(null);
                }
              : undefined
          }
          onBack={() => setScreen(null)}
        />
      );
    }
    if (key === 'category') {
      return (
        <CategoryScreen
          value={draft.category.getOrElse('') || null}
          suggested={[]}
          onPick={(category) => {
            set('category')(some(category));
            setScreen(null);
          }}
          onBack={() => setScreen(null)}
        />
      );
    }
    if (key === 'type') {
      return (
        <TransferTypeScreen
          value={draft.transferType}
          suggested={null}
          onPick={(type) => {
            set('transferType')(type);
            setScreen(null);
          }}
          onBack={() => setScreen(null)}
        />
      );
    }
    return (
      <ScreenFrame
        title="Tags & fields"
        onBack={() => setScreen(null)}
        trailing={
          <button
            type="button"
            onClick={() => setScreen(null)}
            className="cursor-pointer text-sm font-semibold text-blue-600 dark:text-blue-400"
          >
            Done
          </button>
        }
      >
        <TagsAndFieldsInput
          variant="screen"
          tags={draft.tags}
          fieldValues={draft.fieldValues}
          onChange={(tags, fieldValues) =>
            setDraft((prev) => ({...prev, tags, fieldValues}))
          }
        />
      </ScreenFrame>
    );
  };

  const footer = (
    <div className="flex w-full flex-col gap-2">
      {error && (
        <p className="text-destructive text-sm">Couldn&apos;t save: {error.message}</p>
      )}
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={!hasChanges || !valid || loading}
        className="w-full py-3 text-base"
      >
        {loading ? 'Saving…' : 'Save'}
      </Button>
    </div>
  );

  return (
    <Modal
      label={isPayment ? 'Edit payment' : 'Edit transfer'}
      onRequestClose={onRequestClose}
      // The create sheet's frame: a sheet as tall as what it holds on a
      // phone, a centered dialog from sm up.
      className="top-auto bottom-0 h-auto max-h-[92dvh] rounded-b-none sm:rounded-b-lg"
      footer={screen == null ? footer : undefined}
    >
      <form id={formId} onSubmit={handleSubmit}>
        <SheetScreens
          screen={screen}
          renderScreen={renderScreen}
          main={
            <>
              {header}
              {sheetLines}
              {lineList}
            </>
          }
        />
      </form>
    </Modal>
  );
};

